import { getIntl, t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';

type Props = {
    value?: number;
};

export const formatNumber = (num: number, decimalScale: 1 | 2): string => {
    if (decimalScale === 2) {
        return num.toFixed(2);
    }
    // Values carry up to two decimals when two-decimal entry is enabled; a one-decimal
    // display rounds them up. Round to cents first so float noise (1.1 * 10 = 11.000...2)
    // cannot ceil an on-grid value to the next tenth.
    const roundedUp = Math.ceil(Math.round(num * 100) / 10) / 10;
    return roundedUp % 1 === 0 ? roundedUp.toFixed(1) : roundedUp.toString();
};

// The new design's locale writes the rounded hours, grouping the thousands; classic writes the bare number.
export const formatToHours = (value?: number, decimalScale: 1 | 2 = 1): string => {
    const hours = formatNumber(value || 0, decimalScale);
    const shown = isNewDesignCopy()
        ? getIntl().formatNumber(Number(hours), {
              minimumFractionDigits: decimalScale,
              maximumFractionDigits: decimalScale,
          })
        : hours;
    return `${shown}${t('time.hours-suffix')}`;
};

// Decimal hours as their exact hours-minutes equivalent (e.g. 0.2 -> "12 min", 1.5 -> "1 h 30 min").
// Minutes are rounded so off-grid manual entries still show a whole-minute hint. Empty for <= 0.
export const formatHoursToHm = (value?: number): string => {
    const totalMinutes = Math.round((value || 0) * 60);
    if (totalMinutes <= 0) return '';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return [hours ? `${hours}${t('time.hours-suffix')}` : '', minutes ? `${minutes}${t('time.minutes-suffix')}` : '']
        .filter(Boolean)
        .join(' ');
};

const HoursDisplay = ({ value }: Props) => {
    const { displayDecimalScale } = useTimecardPrecision();
    return <span className="whitespace-nowrap tabular-nums">{formatToHours(value, displayDecimalScale)}</span>;
};

export default HoursDisplay;
